import React, { type ReactNode } from 'react';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
  TooltipProvider,
} from '@core-ui/Components/ui/tooltip.tsx';
import { IconChevronRight } from '@tabler/icons-react';

interface SidebarItemProps {
  icon: ReactNode;
  label: string;
  onClick?: () => void;
  subItems?: Array<{ icon?: ReactNode; label: string; onClick?: () => void }>;
  isSidebarOpen: boolean;
  selectedLabel?: string;
  onSelect?: (label: string) => void;
  disabled?: boolean;
}

const SidebarItem: React.FC<SidebarItemProps> = ({
  icon,
  label,
  onClick,
  subItems,
  isSidebarOpen,
  selectedLabel,
  onSelect,
  disabled
}) => {
  const [isOpen, setIsOpen] = React.useState(false);

  const handleClick = (): void => {
    if (subItems != null) {
      setIsOpen(!isOpen);
    } else {
      onClick?.();
      onSelect?.(label);
    }
  };

  const isDirectSelected = selectedLabel === label;
  const isAnySubSelected =
    subItems?.some((item) => item.label === selectedLabel) ?? false;
  const isActive = isDirectSelected || isAnySubSelected;

  const getColors = (): string => {
    if (disabled === true) {
      return 'text-[color:var(--warning-foreground-sidebar)] cursor-not-allowed';
    }
    if (isActive) {
      return 'bg-[color:var(--sidebar-hover-light)] text-white dark:bg-[color:var(--sidebar-hover-dark)]';
    }
    return 'text-white hover:bg-[color:var(--sidebar-hover-light)] hover:dark:bg-[color:var(--sidebar-hover-dark)]';
  };

  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <div
            onClick={handleClick}
            className={`flex items-center ${
              isSidebarOpen
                ? 'justify-start gap-3 px-4 py-2 rounded-lg'
                : 'justify-center w-9 h-9 rounded-md'
            } cursor-pointer transition-colors
            ${getColors()}`}
          >
            <span
              className={`${isSidebarOpen ? '' : 'w-5 h-5 flex justify-center items-center'}`}
            >
              {icon}
            </span>
            {isSidebarOpen && (
              <>
                <span className="whitespace-nowrap">{label}</span>
                {subItems != null && (
                  <span
                    className={`ml-auto transition-transform duration-200 ${
                      isOpen ? 'rotate-90' : ''
                    }`}
                  >
                    <IconChevronRight size={16} />
                  </span>
                )}
              </>
            )}
          </div>
        </TooltipTrigger>

        {!isSidebarOpen && subItems != null && (
          <TooltipContent
            side="right"
            align="start"
            className="bg-popover text-popover-foreground p-2 rounded-md shadow-lg space-y-1 w-48"
          >
            {subItems.map((subItem, index) => {
              const isSubActive = selectedLabel === subItem.label;
              return (
                <div
                  key={index}
                  onClick={() => {
                    subItem.onClick?.();
                    onSelect?.(subItem.label);
                  }}
                  className={`flex items-center gap-2 text-sm px-2 py-1 cursor-pointer rounded-md transition-colors
                  ${
                isSubActive
                  ? 'bg-muted text-foreground'
                  : 'hover:bg-muted hover:text-foreground text-muted-foreground'
                }`}
                >
                  {Boolean(subItem.icon) && (
                    <span className="w-4 h-4">{subItem.icon}</span>
                  )}
                  <span>{subItem.label}</span>
                </div>
              );
            })}
          </TooltipContent>
        )}
      </Tooltip>
    </TooltipProvider>
  );
};

export default SidebarItem;
